'use client';

import React, { useEffect, useRef } from 'react';

export default function PhilosophySection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const elements = sectionRef?.current?.querySelectorAll('.reveal-on-scroll');
    if (!elements) return;

    elements?.forEach((el) => el?.classList?.add('hidden-initially'));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const accentTags = ['Haal · حال', 'Ayat · آيات', 'Taqwah · تقوى', 'Kulli · كلي'];

  return (
    <section
      ref={sectionRef}
      id="philosophy"
      className="relative overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, #1C2B25 0%, #0F1E18 60%, #0A1410 100%)',
        paddingTop: 'clamp(56px, 8vw, 96px)',
        paddingBottom: 'clamp(56px, 8vw, 96px)',
      }}
    >
      {/* Atmospheric background */}
      <div
        className="absolute top-0 right-0 pointer-events-none"
        style={{
          width: 'clamp(200px, 40vw, 400px)',
          height: 'clamp(200px, 40vw, 400px)',
          background: 'radial-gradient(circle, rgba(44,74,62,0.35) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />
      <div
        className="absolute bottom-0 left-0 pointer-events-none"
        style={{
          width: 'clamp(150px, 30vw, 300px)',
          height: 'clamp(150px, 30vw, 300px)',
          background: 'radial-gradient(circle, rgba(139,105,20,0.12) 0%, transparent 70%)',
          filter: 'blur(50px)',
        }}
      />
      <div className="max-w-[1280px] mx-auto px-6 lg:px-8">

        {/* Section label + accent tags */}
        <div className="reveal-on-scroll stagger-1 mb-12 flex flex-wrap items-center gap-3">
          <span
            className="font-mono text-xs tracking-widest uppercase px-4 py-2 rounded"
            style={{
              color: '#7EC8A0',
              background: 'rgba(44,74,62,0.35)',
              border: '1px solid rgba(44,74,62,0.5)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            Framework · الإطار
          </span>
          {accentTags?.map((tag) => (
            <span
              key={tag}
              className="font-mono text-xs tracking-wider px-3 py-1.5 rounded"
              style={{
                color: '#B38A2A',
                background: 'rgba(139,105,20,0.15)',
                border: '1px solid rgba(139,105,20,0.3)',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.65rem',
              }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Asymmetric 60/40 layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20 items-start">

          {/* Left 60% — main content */}
          <div className="lg:col-span-3">

            {/* Large pull quote */}
            <div className="reveal-on-scroll stagger-2 pull-quote-line mb-10">
              <p
                className="font-heading"
                style={{
                  fontSize: 'clamp(1.4rem, 3.2vw, 2.4rem)',
                  lineHeight: 1.25,
                  letterSpacing: '-0.02em',
                  color: '#F5F0E8',
                  fontWeight: 400,
                  fontStyle: 'italic',
                }}
              >
                "A collection of ideas acquired in submission toward Allah — linking people to resources not as ends in themselves, but as{' '}
                <em style={{ color: '#B38A2A' }}>Ayat</em>
                {' '}— signs pointing back to the One."
              </p>
            </div>

            {/* Body paragraphs */}
            <div className="reveal-on-scroll stagger-3 space-y-5">
              <p
                className="font-body leading-relaxed"
                style={{ fontSize: 'clamp(0.9rem, 2vw, 1.05rem)', color: 'rgba(245,240,232,0.7)', lineHeight: 1.8 }}
              >
                The Haal Project began as a personal documentation — a founder writing after coming into deen, recording surreal spiritual experiences, and building a vocabulary drawn from the Islamic lexicon to express and share these insights with others on the path.
              </p>
              <p
                className="font-body leading-relaxed"
                style={{ fontSize: 'clamp(0.9rem, 2vw, 1.05rem)', color: 'rgba(245,240,232,0.7)', lineHeight: 1.8 }}
              >
                <span
                  className="font-mono text-sm"
                  style={{ color: '#B38A2A', fontFamily: 'var(--font-mono)' }}
                >
                  Haal (حال)
                </span>
                {' '}— the Arabic word for state, condition, present reality — names the project's core orientation: attending to where we are right now, in this moment, as a starting point for understanding God's signs all around us.
              </p>
              <p
                className="font-body leading-relaxed"
                style={{ fontSize: 'clamp(0.9rem, 2vw, 1.05rem)', color: 'rgba(245,240,232,0.7)', lineHeight: 1.8 }}
              >
                Everything in this framework is understood through{' '}
                <span
                  className="font-mono text-sm"
                  style={{ color: '#B38A2A', fontFamily: 'var(--font-mono)' }}
                >
                  Ayat (آيات)
                </span>
                {' '}— signs and proofs. Not merely Quranic verses, but the broader Quranic concept: that all of creation is a sign pointing back to its Creator. To recognize these signs is to walk in{' '}
                <span
                  className="font-mono text-sm"
                  style={{ color: '#B38A2A', fontFamily: 'var(--font-mono)' }}
                >
                  Taqwah (تقوى)
                </span>
                {' '}— God-Consciousness.
              </p>
            </div>

            {/* Quranic reference */}
            <div
              className="reveal-on-scroll stagger-4 mt-8 p-6 rounded-card"
              style={{
                background: 'rgba(44,74,62,0.25)',
                border: '1px solid rgba(44,74,62,0.4)',
              }}
            >
              <p
                className="font-heading text-right mb-3"
                style={{ fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)', color: '#7EC8A0', fontStyle: 'italic', lineHeight: 1.6 }}
              >
                سَنُرِيهِمْ آيَاتِنَا فِي الْآفَاقِ وَفِي أَنفُسِهِمْ
              </p>
              <p
                className="font-body text-sm"
                style={{ color: 'rgba(245,240,232,0.5)' }}
              >
                "We will show them Our signs in the horizons and within themselves until it becomes clear to them that it is the truth." — Quran 41:53
              </p>
            </div>
          </div>

          {/* Right 40% — vocabulary definitions */}
          <div className="lg:col-span-2 space-y-4">
            <div className="reveal-on-scroll stagger-2">
              <p
                className="font-mono text-xs tracking-widest uppercase mb-5"
                style={{ color: 'rgba(245,240,232,0.35)', fontFamily: 'var(--font-mono)' }}
              >
                Core Vocabulary
              </p>
            </div>

            {[
              {
                term: 'Haal',
                arabic: 'حال',
                definition: 'State of being. The present spiritual condition. The project takes its name from this — attending to where we are, as the ground from which we understand.',
                delay: 'stagger-2',
                tagColor: '#7EC8A0',
                tagBg: 'rgba(44,74,62,0.3)',
              },
              {
                term: 'Ayat',
                arabic: 'آيات',
                definition: 'Signs, proofs, verses. All of creation as a sign pointing back to Allah. The fabric of reality is woven with Ayat for those with eyes to see.',
                delay: 'stagger-3',
                tagColor: '#B38A2A',
                tagBg: 'rgba(139,105,20,0.2)',
              },
              {
                term: 'Taqwah',
                arabic: 'تقوى',
                definition: 'God-Consciousness. Not fear alone, but a sustained orientation of the heart toward Allah in every state, every moment, every sign encountered.',
                delay: 'stagger-4',
                tagColor: '#7EC8A0',
                tagBg: 'rgba(44,74,62,0.3)',
              },
              {
                term: 'Kulli',
                arabic: 'كلي',
                definition: 'Total, universal, comprehensive. As in Kulli Haal — every state, all conditions. The Keys to Kulli Haal curriculum works from this totality.',
                delay: 'stagger-5',
                tagColor: '#B38A2A',
                tagBg: 'rgba(139,105,20,0.2)',
              },
            ]?.map((item) => (
              <div
                key={item?.term}
                className={`reveal-on-scroll ${item?.delay} p-5 rounded-card`}
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span
                      className="font-mono text-xs tracking-widest uppercase px-2 py-0.5 rounded"
                      style={{ color: item?.tagColor, background: item?.tagBg, border: `1px solid ${item?.tagColor}40`, fontFamily: 'var(--font-mono)', fontSize: '0.6rem' }}
                    >
                      {item?.term}
                    </span>
                  </div>
                  <span
                    className="font-heading text-xl"
                    style={{ color: '#B38A2A', opacity: 0.6, fontStyle: 'italic' }}
                  >
                    {item?.arabic}
                  </span>
                </div>
                <p
                  className="font-body text-sm leading-relaxed"
                  style={{ color: 'rgba(245,240,232,0.55)', lineHeight: 1.65 }}
                >
                  {item?.definition}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}